import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { meQuery } from '@/lib/me'
import type { CostCenter } from './cost-center-tree'

export type CostCenterBody = {
  name: Record<string, string>
  code?: string | null
  kind?: 'department' | 'client' | 'project' | 'other'
  parent_id?: string | null
}

const listKey = (companyId: string | null) =>
  ['companies', companyId, 'accounting', 'cost-centers'] as const

function unwrap<T>(response: { data?: unknown }, endpoint: string): T {
  const body = response.data as { data?: T } | undefined
  if (body?.data === undefined) throw new Error(`${endpoint} returned no data.`)
  return body.data
}

export function useCostCenters({
  activeOnly = false,
  enabled = true,
}: {
  activeOnly?: boolean
  enabled?: boolean
} = {}) {
  const { data: me } = useQuery(meQuery)
  const companyId = me?.active_company_id ?? null
  return useQuery<CostCenter[], Error>({
    queryKey: listKey(companyId),
    enabled: Boolean(companyId) && enabled,
    select: (centers) => (activeOnly ? centers.filter((center) => center.is_active) : centers),
    queryFn: async () => {
      if (!companyId) return []
      return unwrap<CostCenter[]>(
        await api.GET('/accounting/cost-centers', { headers: { 'X-Company-Id': companyId } }),
        'GET /accounting/cost-centers',
      )
    },
  })
}

function useCostCenterMutation<TVariables, TData = void>(
  companyId: string | null,
  mutationFn: (variables: TVariables) => Promise<TData>,
) {
  const queryClient = useQueryClient()
  return useMutation<TData, Error, TVariables>({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: listKey(companyId) }),
  })
}

export function useCreateCostCenter(companyId: string | null) {
  return useCostCenterMutation<CostCenterBody, CostCenter>(companyId, async (body) => {
    const response = await api.POST('/accounting/cost-centers', {
      headers: { 'X-Company-Id': companyId ?? '' },
      body: body as unknown as components['schemas']['CostCenterSaveRequest'],
    })
    return unwrap<CostCenter>(response, 'POST /accounting/cost-centers')
  })
}

export function useUpdateCostCenter(companyId: string | null) {
  return useCostCenterMutation<{ id: string; body: CostCenterBody }, CostCenter>(
    companyId,
    async ({ id, body }) => {
      const response = await api.PATCH('/accounting/cost-centers/{costCenter}', {
        headers: { 'X-Company-Id': companyId ?? '' },
        params: { path: { costCenter: id } },
        body: body as unknown as components['schemas']['CostCenterSaveRequest'],
      })
      return unwrap<CostCenter>(response, 'PATCH /accounting/cost-centers')
    },
  )
}

export function useSetCostCenterActive(companyId: string | null) {
  return useCostCenterMutation<{ id: string; active: boolean }>(
    companyId,
    async ({ id, active }) => {
      if (active) {
        await api.POST('/accounting/cost-centers/{costCenter}/activate', {
          headers: { 'X-Company-Id': companyId ?? '' },
          params: { path: { costCenter: id } },
        })
      } else {
        await api.POST('/accounting/cost-centers/{costCenter}/deactivate', {
          headers: { 'X-Company-Id': companyId ?? '' },
          params: { path: { costCenter: id } },
        })
      }
    },
  )
}

export function useDeleteCostCenter(companyId: string | null) {
  return useCostCenterMutation<string>(companyId, async (id) => {
    await api.DELETE('/accounting/cost-centers/{costCenter}', {
      headers: { 'X-Company-Id': companyId ?? '' },
      params: { path: { costCenter: id } },
    })
  })
}
